import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Copy, Check, Loader2 } from "lucide-react";
import { API } from "@/lib/auth";

export const balanceDue = (e) => {
  const n = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  if (n(e.balance_amount) !== null) return n(e.balance_amount);
  if (n(e.quote_amount) !== null) return n(e.quote_amount) - (e.deposit_paid ? n(e.deposit_amount) || 0 : 0);
  return null;
};

const money = (v) => `£${Number(v).toFixed(2).replace(".00", "")}`;

export default function BridalBalancePanel({ enquiry, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!enquiry.deposit_paid) return null;
  const due = balanceDue(enquiry);
  const payUrl = enquiry.balance_token ? `${window.location.origin}/bridal/pay/${enquiry.balance_token}` : null;

  const send = async () => {
    if (!window.confirm(`Email ${enquiry.full_name} a secure link to pay her balance of ${money(due)}?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/bridal/enquiries/${enquiry.id}/balance-request`);
      onChange(r.data.enquiry);
      toast.success(`Balance link emailed (${money(r.data.amount)})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the link"); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(payUrl); toast.success("Link copied"); } catch { toast.error("Could not copy"); }
  };

  if (enquiry.balance_paid) {
    return (
      <section data-testid="bridal-balance-panel" className="rounded-3xl bg-[#EAF6EE] border border-[#BFE3CB] p-5">
        <div className="overline text-[#1E5B33]">Balance</div>
        <p data-testid="bridal-balance-paid" className="mt-2 text-sm text-[#1E5B33] inline-flex items-center gap-2">
          <Check size={14} /> Paid in full{enquiry.balance_paid_amount ? ` (${money(enquiry.balance_paid_amount)})` : ""}{enquiry.balance_paid_at ? ` on ${new Date(enquiry.balance_paid_at).toLocaleDateString("en-GB")}` : ""}
        </p>
      </section>
    );
  }
  const ready = due !== null && due >= 1;
  return (
    <section data-testid="bridal-balance-panel" className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
      <div className="flex items-baseline justify-between gap-3">
        <div className="overline">Balance</div>
        <div data-testid="bridal-balance-due" className="font-display text-2xl">{ready ? money(due) : "Not set"}</div>
      </div>
      <p className="mt-2 text-xs text-[hsl(var(--onyx)/0.6)]">
        {!ready
          ? "Add the quote total (or a balance amount) under Booking progress to send a balance link."
          : enquiry.balance_requested_at
            ? `Link emailed ${new Date(enquiry.balance_requested_at).toLocaleDateString("en-GB")} — waiting for payment. When she pays, she's marked Paid in Full automatically.`
            : "Uses the balance on this enquiry (or quote minus booking fee). When she pays, she's marked Paid in Full automatically."}
      </p>
      {ready && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={send} disabled={busy} data-testid="bridal-balance-send-btn" className="text-xs px-4 py-2 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {enquiry.balance_requested_at ? "Resend balance email" : "Email balance link"}
          </button>
          {payUrl && <button onClick={copy} data-testid="bridal-balance-copy-btn" className="text-xs px-4 py-2 rounded-full border inline-flex items-center gap-1.5"><Copy size={12} /> Copy link</button>}
        </div>
      )}
    </section>
  );
}
